import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { getMenuPermission, isPublicMenu, getCategoryResources, isPublicCategory } from '@/config/menuPermissions';

interface Permission {
  id: string;
  name: string;
  resource: string;
  action: string;
  description: string;
}

interface RolePermission {
  role: string;
  permission_id: string;
}

export const usePermissions = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchUserPermissions();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchUserPermissions = async () => {
    if (!user) return;

    try {
      // Get all user's roles
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (rolesData && rolesData.length > 0) {
        const roles = rolesData.map(r => r.role as string);
        setAllRoles(roles);
        
        // Check if user has admin or owner role
        const hasAdminRole = roles.includes('admin') || roles.includes('owner');
        setUserRole(hasAdminRole ? 'admin' : roles[0]);

        // Fetch all permissions
        const { data: allPerms } = await supabase
          .from('permissions')
          .select('*');

        if (allPerms) {
          setPermissions(allPerms as Permission[]);

          // If admin, user has all permissions
          if (hasAdminRole) {
            setUserPermissions(allPerms as Permission[]);
          } else {
            // Fetch role permissions for user's roles
            const { data: rolePerms } = await supabase
              .from('role_permissions')
              .select('permission_id')
              .in('role', roles as any);

            if (rolePerms) {
              const permIds = rolePerms.map(rp => rp.permission_id);
              const userPerms = (allPerms as Permission[]).filter(p => permIds.includes(p.id));
              setUserPermissions(userPerms);
            }
          }
        }
      } else {
        setUserRole('user');
        setAllRoles(['user']);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if user can perform an action on a resource
  const can = useCallback((action: string, resource: string): boolean => {
    if (userRole === 'admin' || userRole === 'owner') return true;
    
    return userPermissions.some(
      (perm) => perm.action === action && perm.resource === resource
    );
  }, [userRole, userPermissions]);

  // Check if user has a specific role
  const hasRole = useCallback((role: string | string[]): boolean => {
    if (!userRole && allRoles.length === 0) return false;
    if (Array.isArray(role)) {
      return role.some(r => allRoles.includes(r));
    }
    return allRoles.includes(role);
  }, [userRole, allRoles]);

  // Check if user is admin
  const isAdmin = useCallback((): boolean => {
    return hasRole(['admin', 'owner']);
  }, [hasRole]);

  // Check if user can access a specific menu item by path
  const canAccessMenu = useCallback((path: string): boolean => {
    // Admin/owner has access to everything
    if (userRole === 'admin' || userRole === 'owner' || allRoles.includes('admin') || allRoles.includes('owner')) {
      return true;
    }
    
    // Check if menu is public
    if (isPublicMenu(path)) {
      return true;
    }
    
    // Get the permission resource for this path
    const mapping = getMenuPermission(path);
    if (!mapping) {
      // If no mapping exists, deny access (fail-safe)
      return false;
    }
    
    // Check if user has the permission for this menu
    return userPermissions.some(
      (perm) => perm.resource === mapping.resource && perm.action === 'access'
    );
  }, [userRole, allRoles, userPermissions]);

  // Check if user can access any item in a category
  const canAccessCategory = useCallback((categoryKey: string): boolean => {
    // Admin/owner has access to everything
    if (userRole === 'admin' || userRole === 'owner' || allRoles.includes('admin') || allRoles.includes('owner')) {
      return true;
    }
    
    // Check if category is public
    if (isPublicCategory(categoryKey)) {
      return true;
    }
    
    // Get the permission resources for this category
    const resources = getCategoryResources(categoryKey);
    if (!resources || resources.length === 0) {
      return false;
    }
    
    // Check if user has any permission in this category
    return userPermissions.some(
      (perm) => resources.includes(perm.resource) && perm.action === 'access'
    );
  }, [userRole, allRoles, userPermissions]);

  // Get all accessible menu paths for the user
  const getAccessibleMenuPaths = useCallback((): string[] => {
    if (userRole === 'admin' || userRole === 'owner') {
      // Return all paths
      return [];
    }
    
    return userPermissions
      .filter(p => p.resource.startsWith('menu.') && p.action === 'access')
      .map(p => p.resource);
  }, [userRole, userPermissions]);

  const refreshPermissions = useCallback(async () => {
    setLoading(true);
    await fetchUserPermissions();
  }, [user]);

  return {
    permissions,
    userPermissions,
    userRole,
    allRoles,
    loading,
    can,
    hasRole,
    isAdmin,
    canAccessMenu,
    canAccessCategory,
    getAccessibleMenuPaths,
    refreshPermissions,
  };
};
